import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

// Feature flag to disable session association prompts
const ENABLE_SESSION_ASSOCIATION = false;

interface SessionDemographics {
  timezone: string;
  userAgent: string;
  screenResolution: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  browserName: string;
  browserVersion: string;
  osName: string;
  language: string;
}

export const useSessionTracking = () => {
  const { user } = useAuth();
  const [sessionKey, setSessionKey] = useState<string>('');
  const [hasUnassociatedActivity, setHasUnassociatedActivity] = useState(false);
  const [showAssociationPrompt, setShowAssociationPrompt] = useState(false);

  // Generate or retrieve session key
  useEffect(() => {
    const initializeSession = async () => {
      let key = localStorage.getItem('session_key');
      
      if (!key) {
        key = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('session_key', key);
      }
      
      setSessionKey(key);
      
      // Capture demographics and create/update session record
      const demographics = captureDemographics();
      await upsertSession(key, demographics);
    };
    
    initializeSession();
  }, []);

  // Check for auto-association preference when user logs in
  useEffect(() => {
    const checkAutoAssociation = async () => {
      if (!ENABLE_SESSION_ASSOCIATION) return;
      if (!user || !sessionKey) return;
      
      // Check if user has always-accept preference from any previous session
      const { data: sessions } = await supabase
        .from('user_sessions')
        .select('association_preference')
        .eq('user_id', user.id)
        .eq('association_preference', 'accepted_always')
        .limit(1);
      
      if (sessions && sessions.length > 0) {
        // User has accepted_always preference - auto-associate pending activity
        console.log('Auto-associating pending activity for user with accepted_always preference');
        await supabase.functions.invoke('associate-session', {
          body: { sessionKey, userId: user.id, preference: 'always' }
        });
        return; // Don't show prompt
      }
    };
    
    checkAutoAssociation();
  }, [user, sessionKey]);

  // Check for unassociated activity when user logs in
  useEffect(() => {
    const checkUnassociatedActivity = async () => {
      if (!ENABLE_SESSION_ASSOCIATION) return;
      if (!user || !sessionKey) return;
      
      // Check if prompt was dismissed permanently
      const dismissed = localStorage.getItem('association_prompt_dismissed');
      if (dismissed === 'true') return;
      
      // Check if session already associated
      const { data: session } = await supabase
        .from('user_sessions')
        .select('user_id, association_preference')
        .eq('session_key', sessionKey)
        .maybeSingle();
      
      // Don't show prompt if session already associated or user has always preference
      if (session?.user_id) return;
      
      // Check for pending likes and views with this session (only show prompt for pending)
      const [likesResult, viewsResult] = await Promise.all([
        supabase
          .from('likes')
          .select('id')
          .eq('session_key', sessionKey)
          .eq('association_status', 'pending')
          .limit(1),
        supabase
          .from('views')
          .select('id')
          .eq('session_key', sessionKey)
          .eq('association_status', 'pending')
          .limit(1)
      ]);
      
      if ((likesResult.data && likesResult.data.length > 0) || 
          (viewsResult.data && viewsResult.data.length > 0)) {
        setHasUnassociatedActivity(true);
        setShowAssociationPrompt(true);
      }
    };
    
    checkUnassociatedActivity();
  }, [user, sessionKey]);

  const captureDemographics = (): SessionDemographics => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userAgent = navigator.userAgent;
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    const language = navigator.language;
    
    // Device type detection
    const width = window.screen.width;
    let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
    if (width < 768) deviceType = 'mobile';
    else if (width < 1024) deviceType = 'tablet';
    
    // Browser detection (simple)
    let browserName = 'Unknown';
    let browserVersion = '';
    if (userAgent.includes('Chrome')) {
      browserName = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (userAgent.includes('Firefox')) {
      browserName = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browserName = 'Safari';
      const match = userAgent.match(/Version\/(\d+)/);
      browserVersion = match ? match[1] : '';
    } else if (userAgent.includes('Edge')) {
      browserName = 'Edge';
      const match = userAgent.match(/Edge\/(\d+)/);
      browserVersion = match ? match[1] : '';
    }
    
    // OS detection (simple)
    let osName = 'Unknown';
    if (userAgent.includes('Windows')) osName = 'Windows';
    else if (userAgent.includes('Mac')) osName = 'macOS';
    else if (userAgent.includes('Linux')) osName = 'Linux';
    else if (userAgent.includes('Android')) osName = 'Android';
    else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) osName = 'iOS';
    
    return {
      timezone,
      userAgent,
      screenResolution,
      deviceType,
      browserName,
      browserVersion,
      osName,
      language
    };
  };

  const upsertSession = async (key: string, demographics: SessionDemographics) => {
    try {
      await supabase.functions.invoke('track-session', {
        body: { sessionKey: key, ...demographics }
      });
    } catch (error) {
      console.error('Error upserting session:', error);
    }
  };

  const associateSession = async (dontAskAgain: boolean = false) => {
    if (!user || !sessionKey) return;
    
    try {
      const preference = dontAskAgain ? 'always' : 'once';
      const { error } = await supabase.functions.invoke('associate-session', {
        body: { sessionKey, userId: user.id, preference }
      });
      
      if (error) throw error;
      
      setHasUnassociatedActivity(false);
      setShowAssociationPrompt(false);
      
      // Clear localStorage flag if user chose always
      if (dontAskAgain) {
        localStorage.removeItem('association_prompt_dismissed');
      }
      
      toast.success('Your activity has been linked to your account!');
    } catch (error) {
      console.error('Error associating session:', error);
      toast.error('Failed to link activity');
    }
  };

  const dismissPrompt = async (dontAskAgain: boolean) => {
    if (!sessionKey) return;
    
    try {
      const preference = dontAskAgain ? 'always' : 'once';
      
      // Call decline-association edge function
      const { error } = await supabase.functions.invoke('decline-association', {
        body: { sessionKey, preference }
      });
      
      if (error) {
        console.error('Error declining association:', error);
      }
      
      setShowAssociationPrompt(false);
      setHasUnassociatedActivity(false);
      
      // Set localStorage if user checked "Don't ask me again"
      if (dontAskAgain) {
        localStorage.setItem('association_prompt_dismissed', 'true');
      }
    } catch (error) {
      console.error('Error in dismissPrompt:', error);
    }
  };

  return {
    sessionKey,
    hasUnassociatedActivity,
    showAssociationPrompt,
    associateSession,
    dismissPrompt
  };
};
